import type {
  AdminConfig,
  AdminSession,
  AndroidBuild,
  Branding,
  CatalogLookup,
  ConsumeRequest,
  Movement,
  PhotoIdentification,
  Product,
  ProductInput,
  ProductListEntry,
  Settings,
  ShelfLifePreset,
  StockByBarcode,
  ShoppingListEntry,
  ShoppingListInput,
  StockEntryInput,
  TurboAnswer,
  StockItemUpdate,
  StockItemView,
  Storage,
  StorageInput,
  StorageSummary,
} from '@almox/shared';

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      // O cookie da area administrativa viaja junto (mesma origem).
      credentials: 'same-origin',
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError('OFFLINE', 'Sem conexao com o servidor do Almox.', 0);
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | (T & { error?: { code: string; message: string } })
    | null;

  if (response.status === 401) {
    // Sessao expirada no meio do uso: a tela volta para o login sozinha.
    window.dispatchEvent(new Event('almox:unauthorized'));
  }

  if (!response.ok) {
    const error = payload?.error;
    throw new ApiError(
      error?.code ?? 'ERROR',
      error?.message ?? 'Nao foi possivel completar a acao.',
      response.status,
    );
  }
  return payload as T;
}

const body = (data: unknown): RequestInit['body'] => JSON.stringify(data);

export const api = {
  // --- dispensas ---
  listStorages: () => request<{ storages: StorageSummary[] }>('/storages').then((r) => r.storages),
  getStorageItems: (id: string) =>
    request<{ storage: Storage; items: StockItemView[] }>(`/storages/${id}/items`),
  createStorage: (input: StorageInput) =>
    request<{ storage: Storage }>('/storages', { method: 'POST', body: body(input) }).then(
      (r) => r.storage,
    ),
  updateStorage: (id: string, input: Partial<StorageInput>) =>
    request<{ storage: Storage }>(`/storages/${id}`, { method: 'PATCH', body: body(input) }).then(
      (r) => r.storage,
    ),
  reorderStorages: (ids: string[]) =>
    request<{ storages: StorageSummary[] }>('/storages/reorder', {
      method: 'POST',
      body: body({ ids }),
    }).then((r) => r.storages),
  deleteStorage: (id: string) => request<void>(`/storages/${id}`, { method: 'DELETE' }),

  // --- catalogo ---
  lookupBarcode: (barcode: string) =>
    request<CatalogLookup>(`/catalog/lookup/${encodeURIComponent(barcode)}`),
  /** Catalogo com o estoque de cada produto — base da tela "Produtos". */
  listProducts: (query?: string) =>
    request<{ products: ProductListEntry[] }>(
      `/products${query?.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`,
    ).then((r) => r.products),
  getProduct: (id: string) =>
    request<{ product: Product }>(`/products/${id}`).then((r) => r.product),
  createProduct: (input: ProductInput) =>
    request<{ product: Product }>('/products', { method: 'POST', body: body(input) }).then(
      (r) => r.product,
    ),
  /** Categorias ja usadas, para o autocompletar de classificacao. */
  listCategories: () =>
    request<{ categories: string[] }>('/products/categories').then((r) => r.categories),
  /** Marcas ja usadas, para o autocompletar do campo Marca. */
  listBrands: () => request<{ brands: string[] }>('/products/brands').then((r) => r.brands),
  /** Corrige o produto no catalogo (nome, marca, unidade) em todas as dispensas. */
  updateProduct: (id: string, input: Partial<ProductInput>) =>
    request<{ product: Product }>(`/products/${id}`, { method: 'PATCH', body: body(input) }).then(
      (r) => r.product,
    ),
  /**
   * Le a embalagem por IA. O codigo de barras ja conhecido vai junto, para a
   * resposta voltar vinculada ao produto certo mesmo quando a foto nao mostra
   * o codigo.
   */
  identifyPhoto: (image: string, barcode?: string | null) =>
    request<PhotoIdentification>('/catalog/identify-photo', {
      method: 'POST',
      body: body({ image, ...(barcode ? { barcode } : {}) }),
    }),
  /** Apaga do catalogo. `force` leva junto o estoque que ainda existir. */
  deleteProduct: (id: string, force = false) =>
    request<void>(`/products/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

  // --- estoque ---
  listItems: (params?: { storageId?: string; q?: string }) => {
    const search = new URLSearchParams();
    if (params?.storageId) search.set('storageId', params.storageId);
    if (params?.q) search.set('q', params.q);
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return request<{ items: StockItemView[] }>(`/stock-items${suffix}`).then((r) => r.items);
  },
  listAlerts: () => request<{ items: StockItemView[] }>('/stock-items/alerts').then((r) => r.items),
  /** Onde esta o produto deste codigo — base da baixa por leitura. */
  findStockByBarcode: (barcode: string) =>
    request<StockByBarcode>(`/stock-items/by-barcode/${encodeURIComponent(barcode)}`),
  createEntry: (input: StockEntryInput) =>
    request<{ item: StockItemView }>('/stock-entries', { method: 'POST', body: body(input) }).then(
      (r) => r.item,
    ),
  updateItem: (id: string, input: StockItemUpdate) =>
    request<{ item: StockItemView | null }>(`/stock-items/${id}`, {
      method: 'PATCH',
      body: body(input),
    }).then((r) => r.item),
  consumeItem: (id: string, input: ConsumeRequest) =>
    request<{ item: StockItemView | null; addedToShoppingList: boolean }>(
      `/stock-items/${id}/consume`,
      { method: 'POST', body: body(input) },
    ),
  transferItem: (id: string, toStorageId: string, quantity?: number) =>
    request<{ item: StockItemView }>(`/stock-items/${id}/transfer`, {
      method: 'POST',
      body: body({ toStorageId, ...(quantity ? { quantity } : {}) }),
    }).then((r) => r.item),
  deleteItem: (id: string) => request<void>(`/stock-items/${id}`, { method: 'DELETE' }),

  // --- lista de compras ---
  listShopping: () =>
    request<{ entries: ShoppingListEntry[] }>('/shopping-list').then((r) => r.entries),
  addShopping: (input: ShoppingListInput) =>
    request<{ entry: ShoppingListEntry }>('/shopping-list', {
      method: 'POST',
      body: body(input),
    }).then((r) => r.entry),
  updateShopping: (id: string, input: { status?: 'pending' | 'purchased'; suggestedQuantity?: number }) =>
    request<{ entry: ShoppingListEntry }>(`/shopping-list/${id}`, {
      method: 'PATCH',
      body: body(input),
    }).then((r) => r.entry),
  /** Aceitar varias sugestoes do turbo de uma vez. */
  addShoppingBulk: (entries: ShoppingListInput[]) =>
    request<{ entries: ShoppingListEntry[] }>('/shopping-list/bulk', {
      method: 'POST',
      body: body({ entries }),
    }).then((r) => r.entries),
  removeShopping: (id: string) => request<void>(`/shopping-list/${id}`, { method: 'DELETE' }),
  clearPurchased: () =>
    request<{ removed: number }>('/shopping-list/clear-purchased', { method: 'POST' }),

  // --- modo turbo ---
  /** "Vou fazer X" — a IA confere o estoque e diz o que falta. */
  turboAsk: (prompt: string) =>
    request<TurboAnswer>('/turbo/ask', { method: 'POST', body: body({ prompt }) }),

  // --- configuracoes ---
  getSettings: () => request<{ settings: Settings }>('/settings').then((r) => r.settings),
  updateSettings: (input: Partial<Settings>) =>
    request<{ settings: Settings }>('/settings', { method: 'PATCH', body: body(input) }).then(
      (r) => r.settings,
    ),
  listPresets: () =>
    request<{ presets: ShelfLifePreset[] }>('/shelf-life-presets').then((r) => r.presets),
  savePreset: (category: string, days: number) =>
    request<{ preset: ShelfLifePreset }>('/shelf-life-presets', {
      method: 'POST',
      body: body({ category, days }),
    }).then((r) => r.preset),
  deletePreset: (id: string) => request<void>(`/shelf-life-presets/${id}`, { method: 'DELETE' }),

  listMovements: () => request<{ movements: Movement[] }>('/movements').then((r) => r.movements),

  // --- identidade visual ---
  branding: () => request<Branding>('/branding'),
  saveLogo: (image: string) =>
    request<Branding>('/branding/logo', { method: 'PUT', body: body({ image }) }),
  removeLogo: () => request<Branding>('/branding/logo', { method: 'DELETE' }),

  // --- app android ---
  androidBuild: () => request<AndroidBuild>('/app/android'),

  // --- area administrativa ---
  adminSession: () => request<AdminSession>('/admin/session'),
  adminLogin: (username: string, password: string) =>
    request<AdminSession>('/admin/login', { method: 'POST', body: body({ username, password }) }),
  adminLogout: () => request<{ authenticated: false }>('/admin/logout', { method: 'POST' }),
  adminConfig: () => request<AdminConfig>('/admin/config'),
  saveAdminConfig: (input: { openaiApiKey?: string | null; visionModel?: string | null }) =>
    request<AdminConfig>('/admin/config', { method: 'PATCH', body: body(input) }),
  testOpenAiKey: (openaiApiKey?: string) =>
    request<{ ok: boolean; message: string }>('/admin/openai/test', {
      method: 'POST',
      body: body({ ...(openaiApiKey ? { openaiApiKey } : {}) }),
    }),
  changeAdminPassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: boolean; usingDefaultPassword: boolean }>('/admin/password', {
      method: 'PATCH',
      body: body({ currentPassword, newPassword }),
    }),
};
